// Turns a stored gate check (key + raw detail hash) into the label and plain
// operational sentence the curator reads.

import { GateCheck, GateResult } from './curationScoring';

export const PROFICIENCY_LABELS: Record<number, string> = {
  1: 'Elementary',
  2: 'Limited working',
  3: 'Professional working',
  4: 'Full professional',
  5: 'Native or bilingual',
};

export const STATUS_LABELS: Record<GateResult, string> = {
  fail: 'Fail',
  na: 'N/A',
  pass: 'Pass',
};

const asList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry) => typeof entry === 'string' && entry !== '') : [];

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

export const proficiencyLabel = (level: unknown): string => {
  if (typeof level !== 'number') return 'Not on profile';
  const name = PROFICIENCY_LABELS[level];
  return name ? `${name} (${level}/5)` : `${level}/5`;
};

export const gateCheckLabel = (check: GateCheck): string => {
  if (check.kind === 'language') {
    return asString(check.detail.language_name) || check.key.replace('language:', '').toUpperCase();
  }

  switch (check.key) {
    case 'excluded_regions':
      return 'Excluded regions';
    case 'work_authorization':
      return 'Work authorization';
    case 'eligible_locations':
      return 'Location';
    case 'timezone':
      return 'Timezone';
    default:
      return check.key;
  }
};

const locationDetail = (check: GateCheck): string => {
  const candidate = asString(check.detail.candidate_country) || 'unknown';

  switch (check.key) {
    case 'excluded_regions': {
      const list = asList(check.detail.list);
      if (list.length === 0) return 'No excluded regions set for this job.';
      return `Excluded: ${list.join(', ')} · Candidate country: ${candidate}`;
    }
    case 'eligible_locations': {
      const list = asList(check.detail.list);
      if (list.length === 0) return 'No location restriction set for this job.';
      return `Eligible: ${list.join(', ')} · Candidate country: ${candidate}`;
    }
    case 'timezone': {
      const zones = asList(check.detail.eligible_timezones);
      const candidateZone = asString(check.detail.candidate_timezone) || 'unknown';
      return `Required range: ${zones.join(' to ') || 'not set'} · Candidate: ${candidateZone}`;
    }
    case 'work_authorization':
      return workAuthorizationDetail(check);
    default:
      return '';
  }
};

const workAuthorizationDetail = (check: GateCheck): string => {
  const requirements: string[] = [];
  if (check.detail.citizenship_required === true) requirements.push('work permit or citizenship required');
  if (check.detail.visa_support === true) requirements.push('visa support available');
  if (requirements.length === 0) return 'No work authorization restriction set.';

  const offices = asList(check.detail.office_countries);
  const citizenships = asList(check.detail.candidate_citizenships);
  const where = offices.length > 0 ? ` for ${offices.join(', ')}` : '';
  const held =
    check.detail.has_work_permit === true
      ? 'holds a work permit'
      : `citizenships: ${citizenships.join(', ') || 'unknown'}`;

  return `Job: ${requirements.join(', ')}${where} · Candidate ${held}.`;
};

const languageDetail = (check: GateCheck): string => {
  const required = check.detail.required_level;
  const candidate = check.detail.candidate_level;
  return `Required: ${proficiencyLabel(required)} · Candidate: ${proficiencyLabel(candidate)}`;
};

export const gateCheckDetail = (check: GateCheck): string =>
  check.kind === 'language' ? languageDetail(check) : locationDetail(check);
